/** @typedef {'2023-2024' | '2025-2026'} SchlBenchmarkVersion */

export const SCHL_BENCHMARK_VERSIONS = ['2023-2024', '2025-2026'];

/** @type {SchlBenchmarkVersion} */
export const DEFAULT_SCHL_BENCHMARK_VERSION = '2023-2024';

/** @type {SchlBenchmarkVersion} */
export const LATEST_SCHL_BENCHMARK_VERSION = '2025-2026';

/** Vacancy / bad-debt allowance (% of gross potential income), used with SCHL opex tables. */
export const SCHL_VACANCY_RATE = 3;

const PROVINCE_BY_REGION = {
  atlantic: ['NB', 'NS', 'PE', 'NL'],
  quebec: ['QC'],
  ontario: ['ON'],
  prairies: ['MB', 'SK', 'AB', 'NT', 'NU', 'YT'],
  britishColumbia: ['BC'],
};

const mapRegionsToProvinces = (regions) => {
  const byProvince = {};
  Object.entries(regions).forEach(([regionKey, config]) => {
    PROVINCE_BY_REGION[regionKey].forEach((province) => {
      byProvince[province] = config;
    });
  });
  return byProvince;
};

const rrWood = (elevator = 300) => ({
  appliance: 60,
  heatPump: 190,
  elevator,
});

const rrConcrete = () => ({
  appliance: 60,
  heatPump: 190,
});

const regions2023 = {
  atlantic: {
    woodFrame: {
      small: {
        maintenance: 610,
        managementRate: 4.25,
        salaries: 420,
        otherCostRate: 1,
        replacementReserve: rrWood(300),
      },
      large: {
        maintenance: 610,
        managementRate: 4.25,
        salaries: 420,
        otherCostRate: 1,
        replacementReserve: rrWood(300),
      },
    },
    concrete: {
      any: {
        maintenance: 825,
        managementRate: 4.25,
        salaries: 610,
        otherCostRate: 1.3,
        replacementReserve: rrConcrete(),
      },
    },
  },
  ontario: {
    woodFrame: {
      small: {
        maintenance: 830,
        managementRate: 4.25,
        salaries: 555,
        otherCostRate: 1,
        replacementReserve: rrWood(300),
      },
      large: {
        maintenance: 830,
        managementRate: 4.25,
        salaries: 555,
        otherCostRate: 1,
        replacementReserve: rrWood(300),
      },
    },
    concrete: {
      any: {
        maintenance: 975,
        managementRate: 4.25,
        salaries: 700,
        otherCostRate: 1,
        replacementReserve: rrConcrete(),
      },
    },
  },
  prairies: {
    woodFrame: {
      small: {
        maintenance: 830,
        managementRate: 4.25,
        salaries: 500,
        otherCostRate: 2,
        replacementReserve: rrWood(300),
      },
      large: {
        maintenance: 830,
        managementRate: 4.25,
        salaries: 500,
        otherCostRate: 2,
        replacementReserve: rrWood(300),
      },
    },
    concrete: {
      any: {
        maintenance: 975,
        managementRate: 4.25,
        salaries: 665,
        otherCostRate: 2,
        replacementReserve: rrConcrete(),
      },
    },
  },
  britishColumbia: {
    woodFrame: {
      small: {
        maintenance: 830,
        managementRate: 4.25,
        salaries: 635,
        otherCostRate: 1.7,
        replacementReserve: rrWood(300),
      },
      large: {
        maintenance: 830,
        managementRate: 4.25,
        salaries: 635,
        otherCostRate: 1.7,
        replacementReserve: rrWood(300),
      },
    },
    concrete: {
      any: {
        maintenance: 975,
        managementRate: 4.25,
        salaries: 700,
        otherCostRate: 1.8,
        replacementReserve: rrConcrete(),
      },
    },
  },
  quebec: {
    woodFrame: {
      small: {
        maintenance: 610,
        managementRate: 4.25,
        salaries: 215,
        otherCostRate: 0,
        replacementReserve: rrWood(300),
      },
      large: {
        maintenance: 610,
        managementRate: 5,
        salaries: 365,
        otherCostRate: 0,
        replacementReserve: rrWood(300),
      },
    },
    concrete: {
      any: {
        maintenance: 925,
        managementRate: 5,
        salaries: 610,
        otherCostRate: 0,
        replacementReserve: rrConcrete(),
      },
    },
  },
};

const regions2025 = {
  atlantic: {
    woodFrame: {
      small: {
        maintenance: 700,
        managementRate: 4.5,
        salaries: 460,
        otherCostRate: 1,
        replacementReserve: rrWood(315),
      },
      large: {
        maintenance: 700,
        managementRate: 4.5,
        salaries: 460,
        otherCostRate: 1,
        replacementReserve: rrWood(315),
      },
    },
    concrete: {
      any: {
        maintenance: 930,
        managementRate: 4.25,
        salaries: 670,
        otherCostRate: 1,
        replacementReserve: rrConcrete(),
      },
    },
  },
  quebec: {
    woodFrame: {
      small: {
        maintenance: 700,
        managementRate: 4.5,
        salaries: 250,
        otherCostRate: 1,
        replacementReserve: rrWood(315),
      },
      large: {
        maintenance: 700,
        managementRate: 5,
        salaries: 400,
        otherCostRate: 1,
        replacementReserve: rrWood(315),
      },
    },
    concrete: {
      any: {
        maintenance: 1040,
        managementRate: 5,
        salaries: 670,
        otherCostRate: 1,
        replacementReserve: rrConcrete(),
      },
    },
  },
  ontario: {
    woodFrame: {
      small: {
        maintenance: 940,
        managementRate: 4.5,
        salaries: 610,
        otherCostRate: 1,
        replacementReserve: rrWood(315),
      },
      large: {
        maintenance: 940,
        managementRate: 4.5,
        salaries: 610,
        otherCostRate: 1,
        replacementReserve: rrWood(315),
      },
    },
    concrete: {
      any: {
        maintenance: 1090,
        managementRate: 4.25,
        salaries: 770,
        otherCostRate: 1,
        replacementReserve: rrConcrete(),
      },
    },
  },
  prairies: {
    woodFrame: {
      small: {
        maintenance: 940,
        managementRate: 4.5,
        salaries: 550,
        otherCostRate: 1,
        replacementReserve: rrWood(315),
      },
      large: {
        maintenance: 940,
        managementRate: 4.5,
        salaries: 550,
        otherCostRate: 1,
        replacementReserve: rrWood(315),
      },
    },
    concrete: {
      any: {
        maintenance: 1090,
        managementRate: 4.25,
        salaries: 730,
        otherCostRate: 1,
        replacementReserve: rrConcrete(),
      },
    },
  },
  britishColumbia: {
    woodFrame: {
      small: {
        maintenance: 940,
        managementRate: 4.5,
        salaries: 700,
        otherCostRate: 1,
        replacementReserve: rrWood(315),
      },
      large: {
        maintenance: 940,
        managementRate: 4.5,
        salaries: 700,
        otherCostRate: 1,
        replacementReserve: rrWood(315),
      },
    },
    concrete: {
      any: {
        maintenance: 1090,
        managementRate: 4.25,
        salaries: 770,
        otherCostRate: 1,
        replacementReserve: rrConcrete(),
      },
    },
  },
};

const byVersion = {
  '2023-2024': mapRegionsToProvinces(regions2023),
  '2025-2026': mapRegionsToProvinces(regions2025),
};

export const resolveSchlBenchmarkVersion = (version) => {
  if (version === '2025-2026' || version === '2023-2024') {
    return version;
  }
  return DEFAULT_SCHL_BENCHMARK_VERSION;
};

export const getSchlExpensesByVersion = (version) => {
  const resolved = resolveSchlBenchmarkVersion(version);
  return byVersion[resolved];
};

export const getPropertySchlBenchmarkVersion = (property) =>
  resolveSchlBenchmarkVersion(property?.schlBenchmarkVersion);

export const getSchlConfig = ({
  province,
  structureType = 'woodFrame',
  numberOfUnits = 1,
  schlBenchmarkVersion,
} = {}) => {
  if (!province) return null;
  const map = getSchlExpensesByVersion(schlBenchmarkVersion);
  const provinceConfig = map[province];
  if (!provinceConfig) return null;

  const units = parseInt(numberOfUnits, 10) || 1;
  const base =
    structureType === 'woodFrame'
      ? provinceConfig.woodFrame[units <= 11 ? 'small' : 'large']
      : provinceConfig.concrete.any;
  if (!base) return null;
  return {
    ...base,
    vacancyRate: base.vacancyRate ?? SCHL_VACANCY_RATE,
  };
};

export default getSchlExpensesByVersion(DEFAULT_SCHL_BENCHMARK_VERSION);
